import { EntityLinks } from '@griply/types';
import {
  buildGoalsById,
  getArchivedGoalIds,
  getAutoCompleteSuggestions,
  getGoalLifeAreas,
  getGoalParentIds,
} from '@griply/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import { GoalItem, GoalTreeItem, GoalTreeItemIds, LifeAreaItem } from './types';

type UseFilteredGoalTreeItemsProps = {
  allGoals: Goal[];
  lifeAreas: LifeArea[];
  searchValue: string;
  expandedItems: Set<ID>;
  goalIndexMap: Map<ID, number>;
  hideNoLifeArea?: boolean;
  // Caller-supplied, and required: a UI menu must not fetch, so a caller with no relationships
  // to read states that by passing NO_ENTITY_LINKS rather than by omitting the prop.
  links: EntityLinks;
};

export const useFilteredGoalTreeItems = ({
  allGoals,
  lifeAreas,
  searchValue,
  expandedItems,
  goalIndexMap,
  hideNoLifeArea = false,
  links,
}: UseFilteredGoalTreeItemsProps): GoalTreeItem[] => {
  const { t } = useTranslation();

  // Colour walks the parent chain per goal; memoising on the collections keeps keystrokes free.
  const goalColorsById = useMemo(() => {
    const goalsById = new Map(allGoals.map((goal) => [goal.id, goal]));

    return new Map(
      allGoals.map((goal) => [
        goal.id,
        getLinkedItemColor(goal.id, {
          goals: allGoals,
          lifeAreas,
          links,
          goalsById,
        }),
      ]),
    );
  }, [allGoals, lifeAreas, links]);
  // We need goals map for color resolution
  const goalsMap = useMemo(() => {
    const map = new Map<ID, Goal>();
    allGoals.forEach((g) => map.set(g.id, g));
    return map;
  }, [allGoals]);

  return useMemo(() => {
    const archivedIds = getArchivedGoalIds(allGoals, { links });

    // A completed goal is never a valid move target; its live sub-goals still are.

    // 1. Search Mode: Return flattened results
    if (searchValue) {
      const matchedGoals = getAutoCompleteSuggestions(searchValue, allGoals, [
        'name',
      ]).filter((g) => !archivedIds.has(g.id) && !g.completedAt);
      const matchedLifeAreas = getAutoCompleteSuggestions(
        searchValue,
        lifeAreas,
        ['name'],
      );

      const lifeAreaItems: LifeAreaItem[] = matchedLifeAreas.map((la) => ({
        type: GoalTreeItemIds.LifeArea,
        ...la,
        indentationLevel: 0,
      }));

      const goalItems: GoalItem[] = matchedGoals.map((g) => {
        const [parentId] = getGoalParentIds(g, { links });
        const parentName = parentId ? goalsMap.get(parentId)?.name : undefined;
        const displayColor = goalColorsById.get(g.id) ?? null;

        return {
          ...g,
          type: GoalTreeItemIds.Goal,
          goalType: g.type,
          indentationLevel: 0,
          parentName,
          displayColor,
        };
      });

      // NoLifeArea is usually not searchable by name unless we add it explicitly,
      // generally we don't return it in search results in this app context unless it matches.
      // But preserving existing behavior: existing behavior didn't search NoLifeArea.

      return [...lifeAreaItems, ...goalItems];
    }

    // 2. Tree Mode: Filter & Build Tree
    const activeGoalIds = new Set<ID>();
    const neededParentIds = new Set<ID>();

    // Identify active and needed goals (Linear scan)
    allGoals.forEach((goal) => {
      if (archivedIds.has(goal.id) || goal.completedAt) {
        return;
      }
      activeGoalIds.add(goal.id);
      getGoalParentIds(goal, { links }).forEach((parentId: ID) =>
        neededParentIds.add(parentId),
      );
    });

    const validGoals = allGoals.filter((g) => {
      if (archivedIds.has(g.id) || g.completedAt) {
        return false;
      }
      return activeGoalIds.has(g.id) || neededParentIds.has(g.id);
    });

    // Maps for fast lookups
    const validGoalMap = new Map<ID, Goal>();
    const childrenMap = new Map<ID, Goal[]>();

    validGoals.forEach((g) => {
      validGoalMap.set(g.id, g);
      if (!childrenMap.has(g.id)) {
        childrenMap.set(g.id, []);
      }
    });

    // Populate children
    validGoals.forEach((g) => {
      const [parentId] = getGoalParentIds(g, { links });
      if (!parentId) {
        return;
      }
      if (validGoalMap.has(parentId)) {
        childrenMap.get(parentId)?.push(g);
      }
    });

    // Sort children
    const compareGoals = (a: Goal, b: Goal) => {
      const indexA = goalIndexMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const indexB = goalIndexMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      if (indexA !== indexB) {
        return indexA - indexB;
      }
      return (a.name || '').localeCompare(b.name || '');
    };

    childrenMap.forEach((children) => children.sort(compareGoals));

    // `flattenVisited` guards against a corrupt parent cycle sending the walk infinite.
    const flattenVisited = new Set<ID>();

    const flattenGoalTree = (goal: Goal, level: number): GoalTreeItem[] => {
      if (flattenVisited.has(goal.id)) {
        return [];
      }
      flattenVisited.add(goal.id);

      const children = childrenMap.get(goal.id) || [];
      const hasChildren = children.length > 0;
      const isExpanded = expandedItems.has(goal.id);

      const displayColor = goalColorsById.get(goal.id) ?? null;

      const items: GoalTreeItem[] = [
        {
          ...goal,
          type: GoalTreeItemIds.Goal,
          goalType: goal.type,
          hasChildren,
          indentationLevel: level,
          displayColor,
        },
      ];

      if (isExpanded && hasChildren) {
        children.forEach((child) => {
          items.push(...flattenGoalTree(child, level + 1));
        });
      }

      return items;
    };

    const items: GoalTreeItem[] = [];
    const processedGoalIds = new Set<ID>();

    const goalParentMap = new Map<ID, Goal>();
    allGoals.forEach((g) => goalParentMap.set(g.id, g));
    const goalsById = buildGoalsById(allGoals);

    // For standard tree mode, indentation:
    // Life Area: 0
    // First Level Goal: 1
    // Nested: +1

    const getLifeAreaIds = (goalId: ID): ID[] => {
      const goal = goalParentMap.get(goalId);
      return goal ? getGoalLifeAreas(goal, { goalsById, links }) : [];
    };

    // 3. Process Life Areas
    lifeAreas.forEach((lifeArea) => {
      // Find roots for this life area
      const roots = validGoals.filter((g) => {
        if (!getLifeAreaIds(g.id).includes(lifeArea.id)) {
          return false;
        }
        // It's a root if it has no parents OR its parent is not in our valid set
        const [parentId] = getGoalParentIds(g, { links });
        return !parentId || !validGoalMap.has(parentId);
      });

      const hasChildren = roots.length > 0;
      items.push({
        type: GoalTreeItemIds.LifeArea,
        hasChildren,
        indentationLevel: 0,
        ...lifeArea,
      });

      if (!expandedItems.has(lifeArea.id)) {
        return;
      }

      roots.sort(compareGoals);

      roots.forEach((root) => {
        // Roots under Life Area start at level 1
        const flatTree = flattenGoalTree(root, 1);
        flatTree.forEach((item) => {
          items.push(item);
          if (item.type === GoalTreeItemIds.Goal) {
            processedGoalIds.add(item.id);
          }
        });
      });
    });

    // 4. Process "No Life Area"
    const noLifeAreaRoots = validGoals.filter((g) => {
      if (processedGoalIds.has(g.id)) {
        return false;
      }
      if (getLifeAreaIds(g.id).length) {
        return false;
      }
      const [parentId] = getGoalParentIds(g, { links });
      return !parentId || !validGoalMap.has(parentId);
    });

    noLifeAreaRoots.sort(compareGoals);

    // Without the bucket these goals have no parent row to expand, so they take
    // its place at level 0 — dropping them instead would make a goal that has no
    // life area unreachable in the picker.
    if (hideNoLifeArea) {
      noLifeAreaRoots.forEach((root) =>
        items.push(...flattenGoalTree(root, 0)),
      );
      return items;
    }

    const hasNoLifeAreaChildren = noLifeAreaRoots.length > 0;
    items.push({
      type: GoalTreeItemIds.NoLifeArea,
      id: GoalTreeItemIds.NoLifeArea,
      name: t('general.labels.life-area.no_life_area'),
      hasChildren: hasNoLifeAreaChildren,
      indentationLevel: 0,
    });

    if (expandedItems.has(GoalTreeItemIds.NoLifeArea)) {
      noLifeAreaRoots.forEach((root) => {
        // No Life Area roots also start at level 1 (visual hierarchy consistent with LA roots)
        items.push(...flattenGoalTree(root, 1));
      });
    }

    return items;
  }, [
    goalColorsById,
    links,
    searchValue,
    allGoals,
    lifeAreas,
    goalIndexMap,
    expandedItems,
    goalsMap,
    hideNoLifeArea,
    t,
  ]);
};
